import { useCallback, useMemo } from 'react';

import type { Group } from '@bugbug/core/types/groups';
import type { Step } from '@bugbug/core/types/steps';
import type { Test } from '@bugbug/core/types/tests';
import { store, useAppDispatch, useAppSelector } from '~/modules/store';
import { selectPlaybackCursorPosition } from '~/modules/uiState/uiState.selectors';
import { ACTIONS } from '~/services/toasts/internalServerError';

import {
  populateGroupUpdateToAllInstances,
  testsApi,
  updateCurrentTestQuery,
} from '../../tests.api';
import { showStepsError } from '../../tests.utils';
import { usePlaybackCursorPosition } from '../usePlaybackCursorPosition/usePlaybackCursorPosition';

interface StepPosition {
  groupId: Group['id'];
  atIndex: number;
}

export const useTestStepPositionUpdate = () => {
  const [updateStepsPosition, state] = testsApi.useUpdateStepsPositionMutation();
  const dispatch = useAppDispatch();
  const cursorPosition = useAppSelector(selectPlaybackCursorPosition);
  const { keepLatestPlaybackCursorPosition, refreshPlaybackCursorPosition } =
    usePlaybackCursorPosition();

  const handleUpdateStepsPosition = useCallback(
    async ({
      testId,
      stepId,
      from,
      to,
    }: {
      testId: Test['id'];
      stepId: Step['id'];
      from: StepPosition;
      to: StepPosition;
    }) => {
      const patches = updateCurrentTestQuery(store.getState, dispatch, (draft) => {
        if (draft.steps?.[stepId]) {
          draft.steps[stepId].groupId = to.groupId;
        }

        // Remove at index from source group and its instances
        draft.groups[from.groupId].steps.splice(from.atIndex, 1);
        populateGroupUpdateToAllInstances(draft, { group: draft.groups[from.groupId] });

        // Move to target group on target position
        draft.groups[to.groupId].steps.splice(to.atIndex, 0, stepId);
        populateGroupUpdateToAllInstances(draft, { group: draft.groups[to.groupId] });
      });

      if (cursorPosition?.afterStep) {
        keepLatestPlaybackCursorPosition();
      } else {
        refreshPlaybackCursorPosition();
      }

      const result = updateStepsPosition({
        testId,
        stepId,
        fromGroupId: from.groupId,
        toGroupId: to.groupId,
        fromIndex: from.atIndex,
        toIndex: to.atIndex,
      }).unwrap();

      result.catch((error) => {
        patches.forEach((patch) => patch.undo());
        showStepsError(ACTIONS.UPDATE, error as Error);
      });

      return result;
    },
    [
      cursorPosition?.afterStep,
      dispatch,
      keepLatestPlaybackCursorPosition,
      refreshPlaybackCursorPosition,
      updateStepsPosition,
    ],
  );

  return useMemo(
    () => [handleUpdateStepsPosition, state] as const,
    [handleUpdateStepsPosition, state],
  );
};
